import React, { useEffect, useRef, useState } from 'react';
import { Users } from 'lucide-react';
import { api } from '../../lib/api';
import { CORREO_RE } from '../../lib/calendario';
import { initials } from '../../lib/format';

/**
 * Caja para escribir un correo con sugerencias de la organización (`GET /calendar/personas`): personas y
 * grupos. Al elegir —o al pulsar Enter/coma con un correo válido— llama a `onElegir({correo, nombre, grupo})`
 * y se vacía. Sirve para invitar (varios, chips fuera) y para compartir un calendario (uno).
 */
export default function CampoPersonas({ onElegir, placeholder = 'Añadir personas', excluir = [], testId, autoFocus }) {
  const [texto, setTexto] = useState('');
  const [sug, setSug] = useState({ q: null, personas: [], grupos: [] });
  const [abierto, setAbierto] = useState(false);
  const [activo, setActivo] = useState(0);
  const caja = useRef(null);

  useEffect(() => {
    if (!abierto) return undefined;
    let vivo = true;
    const t = setTimeout(() => {
      api.get('/calendar/personas', { params: { q: texto.trim() } })
        .then(({ data }) => { if (vivo) { setSug({ q: texto.trim(), personas: data.personas || [], grupos: data.grupos || [] }); setActivo(0); } })
        .catch(() => {});
    }, 180);
    return () => { vivo = false; clearTimeout(t); };
  }, [texto, abierto]);

  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => { if (caja.current && !caja.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);

  const ya = new Set(excluir.map((c) => String(c).toLowerCase()));
  const opciones = [
    ...sug.grupos.filter((g) => g.correo && !ya.has(g.correo.toLowerCase())).map((g) => ({ ...g, grupo: true })),
    ...sug.personas.filter((p) => !ya.has(p.correo.toLowerCase())),
  ].slice(0, 8);

  const elegir = (o) => {
    onElegir({ correo: o.correo.toLowerCase(), nombre: o.nombre || '', grupo: !!o.grupo, id: o.id });
    setTexto('');
    setActivo(0);
    setAbierto(false);   // que no tape la lista de invitados; se vuelve a abrir al escribir
  };

  const confirmarTexto = () => {
    const limpio = texto.trim().replace(/[,;]$/, '').toLowerCase();
    // Un correo escrito entero manda sobre las sugerencias (que pueden ser de lo tecleado hace un momento)
    if (CORREO_RE.test(limpio)) {
      elegir(opciones.find((o) => o.correo.toLowerCase() === limpio) || { correo: limpio, nombre: '' });
      return true;
    }
    if (abierto && sug.q === texto.trim() && opciones[activo]) { elegir(opciones[activo]); return true; }
    return false;
  };

  return (
    <div className="cal-personas" ref={caja}>
      <input
        type="email"
        value={texto}
        autoFocus={autoFocus}
        onChange={(e) => { setTexto(e.target.value); setAbierto(true); }}
        onFocus={() => setAbierto(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setActivo((a) => Math.min(a + 1, opciones.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActivo((a) => Math.max(a - 1, 0)); }
          else if (e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === 'Tab') {
            if (texto.trim() && confirmarTexto()) e.preventDefault();
          } else if (e.key === 'Escape') setAbierto(false);
        }}
        onBlur={() => { if (CORREO_RE.test(texto.trim())) confirmarTexto(); }}
        placeholder={placeholder}
        aria-label={placeholder}
        className="cal-input"
        autoComplete="off"
        data-testid={testId}
      />
      {abierto && opciones.length > 0 && (
        <div className="cal-sugerencias" role="listbox">
          {opciones.map((o, i) => (
            <button
              type="button"
              key={`${o.grupo ? 'g' : 'p'}-${o.correo}`}
              role="option"
              aria-selected={i === activo}
              className={`cal-sugerencia ${i === activo ? 'is-activa' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); elegir(o); }}
              onMouseEnter={() => setActivo(i)}
            >
              <span className="cal-avatar">{o.grupo ? <Users className="h-3.5 w-3.5" /> : initials(o.nombre, o.correo)}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{o.nombre || o.correo}</span>
                <span className="block truncate text-[12px]" style={{ color: 'var(--text-3)' }}>
                  {o.grupo ? `Grupo · ${o.correo}` : o.correo}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
